import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import type {
  DailySnapshotRow,
  CampaignSnapshotRow,
  AdGroupSnapshotRow,
  RsaAssetRow,
  AdSnapshotRow,
  KeywordSnapshotRow,
  RestaurantRow,
  AiInsightRow,
  PageAuditRow,
  CompetitorAuditRow,
  ConversionActionRow,
} from '@/lib/supabase-to-sem';
import { buildRestaurantSemData, aggregateSnapshots, comparePeriods } from '@/lib/supabase-to-sem';
import type { RestaurantSemData, SemSummary } from '@/types/sem';

export type DateRangeKey = '7d' | '30d' | '60d' | '90d' | 'custom';

export interface CustomDateRange {
  start: string; // YYYY-MM-DD
  end: string;
}

const RANGE_DAYS: Record<Exclude<DateRangeKey, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '60d': 60,
  '90d': 90,
};

function getDateRange(rangeKey: DateRangeKey, custom?: CustomDateRange): { start: string; end: string; prevStart: string } {
  if (rangeKey === 'custom' && custom) {
    const startDate = new Date(custom.start + 'T00:00:00');
    const endDate = new Date(custom.end + 'T00:00:00');
    const days = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const prevStart = new Date(startDate);
    prevStart.setDate(prevStart.getDate() - days);
    return {
      start: custom.start,
      end: custom.end,
      prevStart: prevStart.toISOString().split('T')[0],
    };
  }

  const days = RANGE_DAYS[rangeKey as Exclude<DateRangeKey, 'custom'>] || 30;
  const today = new Date();
  const end = new Date(today);
  end.setDate(end.getDate() - 1); // yesterday

  const start = new Date(end);
  start.setDate(start.getDate() - days + 1);

  const prevStart = new Date(start);
  prevStart.setDate(prevStart.getDate() - days);

  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
    prevStart: prevStart.toISOString().split('T')[0],
  };
}

/**
 * Fallback: load static JSON data when Supabase is empty or unconfigured.
 * This preserves access to already-ingested historical data.
 */
async function loadStaticFallback(): Promise<RestaurantSemData[] | null> {
  try {
    const res = await fetch('/data/all-restaurants-sem.json');
    if (!res.ok) return null;
    return (await res.json()) as RestaurantSemData[];
  } catch {
    return null;
  }
}

export function useSemData(rangeKey: DateRangeKey = '30d', customRange?: CustomDateRange) {
  const [restaurants, setRestaurants] = useState<RestaurantSemData[]>([]);
  const [dailySnapshots, setDailySnapshots] = useState<DailySnapshotRow[]>([]);
  const [prevSnapshots, setPrevSnapshots] = useState<DailySnapshotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'static'>('supabase');

  const dateRange = useMemo(() => getDateRange(rangeKey, customRange), [rangeKey, customRange?.start, customRange?.end]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        // If Supabase is not configured, skip straight to static fallback
        if (!supabase) {
          const staticData = await loadStaticFallback();
          if (staticData && staticData.length > 0) {
            setRestaurants(staticData);
            setDailySnapshots([]);
            setPrevSnapshots([]);
            setDataSource('static');
            setLoading(false);
            return;
          }
          setError('No SEM data available. Connect Supabase or check static data files.');
          setLoading(false);
          return;
        }

        // Phase 1: Fetch restaurants + small tables that won't hit 1000-row limit
        const [restaurantRes, snapshotRes, prevSnapshotRes, insightsRes, pageAuditRes, competitorAuditRes] =
          await Promise.all([
            supabase.from('restaurants').select('*'),
            supabase
              .from('daily_snapshots')
              .select('*')
              .gte('date', dateRange.start)
              .lte('date', dateRange.end)
              .order('date', { ascending: true }),
            supabase
              .from('daily_snapshots')
              .select('*')
              .gte('date', dateRange.prevStart)
              .lt('date', dateRange.start)
              .order('date', { ascending: true }),
            supabase
              .from('ai_insights')
              .select('*')
              .order('sync_date', { ascending: false })
              .limit(100),
            supabase
              .from('page_audits')
              .select('id,restaurant_id,page_url,screenshot_path,viewport_width,viewport_height,fold_y,element_map,annotations,overall_score,issue_count,total_revenue_at_risk,model_used,analyzed_at')
              .order('analyzed_at', { ascending: false })
              .limit(50),
            supabase
              .from('competitor_audits')
              .select('id,restaurant_id,keyword,competitor_domain,competitor_name,competitor_ad_copy,competitor_page_url,competitor_screenshot_path,your_score,competitor_score,element_comparison,ai_analysis,total_gap_cost,model_used,analyzed_at')
              .order('analyzed_at', { ascending: false })
              .limit(100),
          ]);

        // If Supabase has restaurant data with snapshots, use it
        const hasSupabaseData =
          !restaurantRes.error &&
          !snapshotRes.error &&
          restaurantRes.data &&
          restaurantRes.data.length > 0 &&
          snapshotRes.data &&
          snapshotRes.data.length > 0;

        if (hasSupabaseData) {
          const restaurantRows = restaurantRes.data as RestaurantRow[];
          const snapshots = snapshotRes.data as DailySnapshotRow[];
          const prevSnaps = (prevSnapshotRes.data || []) as DailySnapshotRow[];
          const allInsights = (insightsRes.data || []) as AiInsightRow[];
          const allPageAudits = (pageAuditRes.data || []) as PageAuditRow[];
          const allCompetitorAudits = (competitorAuditRes.data || []) as CompetitorAuditRow[];

          // Phase 2: Fetch large tables PER-RESTAURANT to avoid 1000-row truncation
          // Each per-restaurant query uses the restaurant_id+date index
          const perRestaurantData = await Promise.all(
            restaurantRows.map(async (r) => {
              const [campRes, agRes, adRes, rsaRes, convRes] = await Promise.all([
                supabase!.from('campaign_snapshots').select('*')
                  .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end),
                supabase!.from('ad_group_snapshots').select('*')
                  .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end),
                supabase!.from('ad_snapshots').select('*')
                  .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end),
                supabase!.from('rsa_asset_snapshots').select('*')
                  .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end),
                supabase!.from('conversion_action_snapshots').select('*')
                  .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end),
              ]);
              return {
                id: r.id,
                campaigns: (campRes.data || []) as CampaignSnapshotRow[],
                adGroups: (agRes.data || []) as AdGroupSnapshotRow[],
                ads: (adRes.data || []) as AdSnapshotRow[],
                rsa: (rsaRes.data || []) as RsaAssetRow[],
                convActions: (convRes.data || []) as ConversionActionRow[],
              };
            })
          );

          // Phase 3: Fetch keywords SEQUENTIALLY to avoid concurrent statement timeouts
          // The keyword_snapshots table is huge; ordering by clicks DESC requires a full sort
          const keywordsByRestaurant = new Map<string, KeywordSnapshotRow[]>();
          for (const r of restaurantRows) {
            const { data } = await supabase!.from('keyword_snapshots').select('*')
              .eq('restaurant_id', r.id).gte('date', dateRange.start).lte('date', dateRange.end)
              .order('clicks', { ascending: false }).limit(500);
            keywordsByRestaurant.set(r.id, ((data || []) as KeywordSnapshotRow[]).filter(k => k.clicks > 0));
          }

          // Index per-restaurant data by restaurant id
          const dataByRestaurant = new Map(perRestaurantData.map(d => [d.id, d]));

          setDailySnapshots(snapshots);
          setPrevSnapshots(prevSnaps);

          const built = restaurantRows.map((r) => {
            const rd = dataByRestaurant.get(r.id);
            return buildRestaurantSemData(
              r,
              snapshots.filter((s) => s.restaurant_id === r.id),
              rd?.campaigns || [],
              rd?.adGroups || [],
              rd?.rsa || [],
              rd?.ads || [],
              keywordsByRestaurant.get(r.id) || [],
              { start: dateRange.start, end: dateRange.end },
              allInsights.filter((ins) => ins.restaurant_id === r.id),
              allPageAudits.filter((pa) => pa.restaurant_id === r.id),
              allCompetitorAudits.filter((ca) => ca.restaurant_id === r.id),
              rd?.convActions || [],
            );
          });

          setRestaurants(built);
          setDataSource('supabase');
          setLoading(false);
          return;
        }

        // Fallback to static JSON data
        const staticData = await loadStaticFallback();
        if (staticData && staticData.length > 0) {
          setRestaurants(staticData);
          setDailySnapshots([]);
          setPrevSnapshots([]);
          setDataSource('static');
          setLoading(false);
          return;
        }

        // Neither source has data
        setError('No SEM data available. Connect Supabase or check static data files.');
        setLoading(false);
      } catch (err: any) {
        // Supabase failed — try static fallback
        const staticData = await loadStaticFallback();
        if (staticData && staticData.length > 0) {
          setRestaurants(staticData);
          setDailySnapshots([]);
          setPrevSnapshots([]);
          setDataSource('static');
          setLoading(false);
          return;
        }
        setError(err.message || 'Failed to fetch SEM data');
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange.start, dateRange.end, dateRange.prevStart]);

  // Aggregate summary across all restaurants
  const aggregatedSummary = useMemo((): SemSummary => {
    return aggregateSnapshots(dailySnapshots);
  }, [dailySnapshots]);

  return {
    restaurants,
    dailySnapshots,
    prevSnapshots,
    aggregatedSummary,
    dateRange,
    loading,
    error,
    dataSource,
    comparePeriods,
  };
}
